<template>
  <b-modal
    :title="isResend ? 'Resend Invitation' : 'Invite to Workforce'"
    centered
    hide-footer
    v-model="show"
    class="pa-4"
    :style="'width: 708px !important;'"
    @hide="onHide"
  >
    <div v-if="candidate">
      <p class="mt-0 mb-3">
        <template v-if="isResend">
          <b>{{ candidate.name || candidate.full_name }}</b> declined the
          previous invitation. Resend a new one?
        </template>
        <template v-else>
          Invite <b>{{ candidate.name || candidate.full_name }}</b> to join your
          workforce?
        </template>
      </p>
      <p class="text-muted small mb-3">
        They'll be notified and can accept or decline. Once accepted, you can
        <template v-if="isFree">add them to your active workforce.</template>
        <template v-else
          >apply a token to add them to your active workforce.</template
        >
      </p>
    </div>

    <b-alert :show="!!error" variant="danger" class="mb-3">{{ error }}</b-alert>

    <div class="d-flex justify-content-center pt-2" style="gap: 0.5rem">
      <b-button variant="primary" :disabled="busy" @click="onConfirm">
        <b-spinner v-if="busy" small class="mr-1" />
        {{ isResend ? 'Resend Invitation' : 'Send Invitation' }}
      </b-button>
      <b-button variant="outline-secondary" :disabled="busy" @click="onClose">
        Cancel
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'WorkforceInvitationModal',

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    candidate: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      busy: false,
      error: '',
    }
  },

  computed: {
    ...mapGetters('auth', ['user']),

    // Free tier / trial — no token step for these companies (the API applies
    // its free bypass), so the helper text drops the token mention.
    isFree() {
      return !!(this.user && this.user.is_free)
    },

    show: {
      get() {
        return this.showModal
      },
      set() {
        this.$emit('closeModal')
      },
    },

    // WorkforceInviteButton only reaches this modal's @invite handler when
    // it's enabled — which for 'declined' means resend, not a first send.
    isResend() {
      return (
        !!this.candidate &&
        this.candidate.workforce_invitation_status === 'declined'
      )
    },
  },

  watch: {
    showModal(val) {
      if (val) this.error = ''
    },
  },

  methods: {
    ...mapActions('candidate', ['sendWorkforceInvitation']),

    onHide() {
      this.$emit('closeModal')
    },

    onClose() {
      this.$emit('closeModal')
    },

    async onConfirm() {
      if (!this.candidate || this.busy) return
      this.busy = true
      this.error = ''
      try {
        await this.sendWorkforceInvitation(this.candidate.id)
        this.$emit('sent', this.candidate.id)
      } catch (err) {
        this.error =
          (err && err.data && err.data.message) ||
          'Failed to send workforce invitation.'
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
strong {
  color: inherit;
}
</style>
